import { useMemo, useState } from "react"
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels"
import { MdDragIndicator } from "react-icons/md"
import ReactJson from "react-json-view"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { oneLight } from "react-syntax-highlighter/dist/cjs/styles/prism"
import { prettyXml, summarizeWsdl, summarizeXsd, xmlToJson, xmlToPrettyJson } from "./xsdFormat"

const HIGHLIGHT_LIMIT = 180_000

const codeStyle = {
    margin: 0,
    padding: "0.75rem 0.85rem 1.5rem",
    background: "transparent",
    fontSize: "0.78rem",
    lineHeight: 1.5,
}

const XsdPreview = ({ text, ext }) => {
    const isWsdl = ext === "wsdl"
    const pretty = useMemo(() => prettyXml(text), [text])
    const jsonText = useMemo(() => xmlToPrettyJson(text), [text])
    const json = useMemo(() => xmlToJson(text), [text])
    const xsd = useMemo(() => summarizeXsd(text), [text])
    const wsdl = useMemo(() => (isWsdl ? summarizeWsdl(text) : null), [isWsdl, text])
    const summary = wsdl || xsd

    return (
        <div className="hrp-docs__xsd">
            <div className="hrp-docs__xsd-meta">
                {(summary.name || summary.targetNamespace) && (
                    <p>
                        {summary.name && (
                            <>
                                <span>{isWsdl ? "Definition" : "Schema"}</span>
                                <code>{summary.name}</code>
                                {summary.targetNamespace ? " · " : ""}
                            </>
                        )}
                        {summary.targetNamespace && (
                            <>
                                <span>Namespace</span>
                                <code>{summary.targetNamespace}</code>
                            </>
                        )}
                    </p>
                )}
                <p>
                    {isWsdl
                        ? `${summary.services.length} services · ${summary.operations.length} operations · ${summary.messages.length} messages · ${summary.elements.length} elements`
                        : `${summary.elements.length} elements · ${summary.complexTypes.length} complex types · ${summary.simpleTypes.length} simple types`}
                </p>
            </div>
            {isWsdl && (
                <>
                    <NameList label="Services" names={summary.services} />
                    <NameList label="Port types" names={summary.portTypes} />
                    <NameList label="Operations" names={summary.operations} />
                    <NameList label="Messages" names={summary.messages} />
                    <NameList label="Bindings" names={summary.bindings} />
                </>
            )}
            <NameList label="Elements" names={summary.elements} />
            <NameList label="Complex types" names={summary.complexTypes} />
            <NameList label="Simple types" names={summary.simpleTypes} />
            <PanelGroup
                autoSaveId="hrp-docs-schema-json"
                direction="horizontal"
                className="hrp-docs__xsd-split"
            >
                <Panel defaultSize={55} minSize={28} className="hrp-docs__xsd-pane">
                    <header>{isWsdl ? "WSDL" : ext === "xml" ? "XML" : "XSD"}</header>
                    <CodeBlock text={pretty} language="markup" />
                </Panel>
                <PanelResizeHandle className="resize-handle hrp-docs__resize">
                    <MdDragIndicator size="1.1rem" />
                </PanelResizeHandle>
                <Panel defaultSize={45} minSize={24} className="hrp-docs__xsd-pane">
                    <JsonPane data={json} text={jsonText} />
                </Panel>
            </PanelGroup>
        </div>
    )
}

export const JsonPreview = ({ text }) => {
    const parsed = useMemo(() => {
        try {
            return { data: JSON.parse(text), error: null }
        } catch (err) {
            return { data: null, error: err instanceof Error ? err.message : "Invalid JSON" }
        }
    }, [text])
    const pretty = useMemo(() => {
        if (parsed.error) return text
        try {
            return JSON.stringify(parsed.data, null, 2)
        } catch {
            return text
        }
    }, [parsed, text])

    if (parsed.error) {
        return (
            <>
                <p className="hrp-docs__status">Invalid JSON. Showing source.</p>
                <pre className="hrp-docs__source">{text}</pre>
            </>
        )
    }

    return (
        <div className="hrp-docs__xsd">
            <div className="hrp-docs__xsd-pane hrp-docs__json-pane">
                <JsonPane
                    data={parsed.data !== null && typeof parsed.data === "object" ? parsed.data : { value: parsed.data }}
                    text={pretty}
                />
            </div>
        </div>
    )
}

const JsonPane = ({ data, text }) => {
    const [collapsed, setCollapsed] = useState(1)
    const [copied, setCopied] = useState(false)

    const copy = async () => {
        if (!text) return
        try {
            await navigator.clipboard.writeText(text)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        } catch {
            /* clipboard may be blocked */
        }
    }

    return (
        <>
            <header className="hrp-docs__xsd-pane-head">
                <span>JSON</span>
                <div>
                    <button type="button" onClick={() => setCollapsed(true)}>Collapse</button>
                    <button type="button" onClick={() => setCollapsed(false)}>Expand</button>
                    <button type="button" onClick={copy} disabled={!text}>
                        {copied ? "Copied" : "Copy"}
                    </button>
                </div>
            </header>
            <div className="hrp-docs__xsd-json">
                {data && typeof data === "object" ? (
                    <ReactJson
                        key={String(collapsed)}
                        src={data}
                        name={false}
                        collapsed={collapsed}
                        collapseStringsAfterLength={80}
                        displayDataTypes={false}
                        displayObjectSize
                        enableClipboard
                        sortKeys={false}
                        quotesOnKeys={false}
                    />
                ) : (
                    <p className="hrp-docs__status">Could not convert this file to JSON.</p>
                )}
            </div>
        </>
    )
}

const CodeBlock = ({ text, language }) => {
    const [forceHighlight, setForceHighlight] = useState(false)
    const large = text.length > HIGHLIGHT_LIMIT
    const highlight = Boolean(text) && (!large || forceHighlight)
    return (
        <div className="hrp-docs__xsd-code">
            {large && (
                <button type="button" onClick={() => setForceHighlight((v) => !v)}>
                    {forceHighlight ? "Plain source" : "Highlight"}
                </button>
            )}
            {highlight ? (
                <SyntaxHighlighter
                    language={language}
                    style={oneLight}
                    showLineNumbers
                    wrapLongLines
                    PreTag="div"
                    customStyle={codeStyle}
                >
                    {text}
                </SyntaxHighlighter>
            ) : (
                <pre className="hrp-docs__source">{text}</pre>
            )}
        </div>
    )
}

const NameList = ({ label, names }) => {
    const [open, setOpen] = useState(false)
    if (!names.length) return null
    const shown = open ? names : names.slice(0, 16)
    return (
        <section className="hrp-docs__xsd-list">
            <header>
                <strong>{label}</strong>
                <em>{names.length}</em>
            </header>
            <div>
                {shown.map((name) => (
                    <code key={name}>{name}</code>
                ))}
                {names.length > 16 && (
                    <button type="button" onClick={() => setOpen((v) => !v)}>
                        {open ? "Show less" : `+${names.length - 16} more`}
                    </button>
                )}
            </div>
        </section>
    )
}

export default XsdPreview
